import { Injectable } from '@angular/core';
import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';
import { PlatformService } from './platform.service';
import { StatusBar } from '@capacitor/status-bar';

@Injectable({
    providedIn: 'root'
})
export class CameraService {

    constructor() { }

    public async capture(): Promise<Photo> {
        const photo = await Camera.getPhoto({
            resultType: CameraResultType.Base64,
            source: CameraSource.Camera,
            quality: 50,
        });

        // Display content under transparent status bar
        if (PlatformService.isIOS()) {
            StatusBar.setOverlaysWebView({ overlay: true });
        }

        return photo;
    }

    // Warning !! Below code are directly generated by copilot and may not fully tested
    /**
     * Resize a Photo object
     * @param photo - The Photo object from Capacitor Camera
     * @param options - Transform options (resize, quality)
     * @returns Promise<Photo> - The transformed photo as Photo object
     */
    public async transformPhoto(photo: Photo, options: PhotoTransformOptions = {}): Promise<Photo> {
        const {
            maxWidth,
            maxHeight,
            maxLongestDimension,
            quality = 0.8,
            format = 'jpeg'
        } = options;

        // Create image element from photo
        const imageElement = await this.createImageFromPhoto(photo);

        // Calculate new dimensions
        const dimensions = this.calculateDimensions(
            imageElement.width,
            imageElement.height,
            maxWidth,
            maxHeight,
            maxLongestDimension
        );

        // Create canvas and apply transformations
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        if (!context) {
            throw new Error('Failed to create 2D context on canvas element');
        }

        // Set canvas dimensions
        canvas.width = dimensions.width;
        canvas.height = dimensions.height;

        // Draw and resize image
        context.drawImage(
            imageElement,
            0,
            0,
            dimensions.width,
            dimensions.height
        );

        // Convert to base64
        const mimeType = `image/${format}`;
        const dataUrl = canvas.toDataURL(mimeType, quality);
        const base64String = dataUrl.split(',')[1];

        // Create blob URL for webPath
        const blob = this.dataURLToBlob(dataUrl);
        const webPath = URL.createObjectURL(blob);

        // Return as Photo object
        return {
            base64String: base64String,
            dataUrl: dataUrl,
            format: format,
            webPath: webPath,
            saved: false
        };
    }

    /**
     * Resize photo by constraining the longest dimension
     * @param photo - The Photo object
     * @param maxLongestDimension - Maximum pixels for the longest side
     * @param quality - Image quality (0.1 to 1.0)
     * @returns Promise<Photo>
     */
    public async resizePhoto(
        photo: Photo,
        maxLongestDimension: number,
        quality: number = 0.8
    ): Promise<Photo> {
        return this.transformPhoto(photo, { maxLongestDimension, quality });
    }

    /**
     * Resize photo to specified dimensions (legacy method for backward compatibility)
     * @param photo - The Photo object
     * @param maxWidth - Maximum width
     * @param maxHeight - Maximum height
     * @param quality - Image quality (0.1 to 1.0)
     * @returns Promise<Photo>
     */
    public async resizePhotoByDimensions(
        photo: Photo,
        maxWidth: number,
        maxHeight: number,
        quality: number = 0.8
    ): Promise<Photo> {
        return this.transformPhoto(photo, { maxWidth, maxHeight, quality });
    }

    /**
     * Create an Image element from Photo object
     * @param photo - The Photo object
     * @returns Promise<HTMLImageElement>
     */
    private async createImageFromPhoto(photo: Photo): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const img = new Image();

            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error('Failed to load image'));

            // Use base64 data if available, otherwise use webPath
            if (photo.base64String) {
                if (!photo.base64String.startsWith('data:image')) {
                    img.src = `data:image/jpeg;base64,${photo.base64String}`;
                } else {
                    img.src = photo.base64String;
                }
            } else if (photo.webPath) {
                img.src = photo.webPath;
            } else {
                reject(new Error('No image data available in Photo object'));
            }
        });
    }

    /**
     * Calculate dimensions for resizing
     * @param originalWidth - Original image width
     * @param originalHeight - Original image height
     * @param maxWidth - Maximum width constraint
     * @param maxHeight - Maximum height constraint
     * @param maxLongestDimension - Maximum pixels for longest dimension
     * @returns Calculated dimensions
     */
    private calculateDimensions(
        originalWidth: number,
        originalHeight: number,
        maxWidth?: number,
        maxHeight?: number,
        maxLongestDimension?: number
    ) {
        let width = originalWidth;
        let height = originalHeight;

        // Priority 1: Resize by longest dimension
        if (maxLongestDimension) {
            const longestSide = Math.max(width, height);
            if (longestSide > maxLongestDimension) {
                const ratio = maxLongestDimension / longestSide;
                width = Math.floor(width * ratio);
                height = Math.floor(height * ratio);
            }
        }
        // Priority 2: Apply width/height constraints (legacy behavior)
        else if (maxWidth || maxHeight) {
            const widthRatio = maxWidth ? maxWidth / width : 1;
            const heightRatio = maxHeight ? maxHeight / height : 1;
            const ratio = Math.min(widthRatio, heightRatio);

            width = Math.floor(width * ratio);
            height = Math.floor(height * ratio);
        }

        return {
            width,
            height
        };
    }

    /**
     * Convert data URL to Blob
     * @param dataURL - Data URL string
     * @returns Blob object
     */
    private dataURLToBlob(dataURL: string): Blob {
        const arr = dataURL.split(',');
        const mime = arr[0].match(/:(.*?);/)![1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);

        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }

        return new Blob([u8arr], { type: mime });
    }

    /**
     * Convert a Photo object into a File
     * @param photo - The Photo object containing base64 or dataUrl
     * @returns File - The converted File object, ready for upload or attachment
     * @throws Error if the Photo does not contain a valid dataUrl
     */
    public photoToFile(photo: Photo, fileName: string = ''): File {
      if (!photo.dataUrl) {
        throw new Error('Photo is missing dataUrl');
      }

      const blob = this.dataURLToBlob(photo.dataUrl);
      const ext = photo.format || 'jpeg';
      if (fileName.length == 0) fileName = `photo_${Date.now()}.${ext}`;
      return new File([blob], fileName, { type: `image/${ext}` });
    }

    /**
     * Get image dimensions from Photo object
     * @param photo - The Photo object
     * @returns Promise with width and height
     */
    public async getPhotoDimensions(photo: Photo): Promise<{ width: number; height: number }> {
        const imageElement = await this.createImageFromPhoto(photo);
        return {
            width: imageElement.width,
            height: imageElement.height
        };
    }

    /**
     * Convert Photo to different format
     * @param photo - The Photo object
     * @param format - Target format ('jpeg', 'png', 'webp')
     * @param quality - Image quality (0.1 to 1.0)
     * @returns Promise<Photo>
     */
    public async convertPhotoFormat(
        photo: Photo,
        format: 'jpeg' | 'png' | 'webp',
        quality: number = 0.8
    ): Promise<Photo> {
        return this.transformPhoto(photo, { format, quality });
    }
}

export interface PhotoTransformOptions {
    maxWidth?: number;
    maxHeight?: number;
    maxLongestDimension?: number; // New option for longest dimension constraint
    quality?: number; // 0.1 to 1.0
    format?: 'jpeg' | 'png' | 'webp';
}